import { useCallback, useRef, useState } from "react";
import { ApiError, type AdminApi } from "@/api";
import { Badge, Banner, Button, Card } from "@/components/ui";
import type { CandidateSourceEnvironment, FeedbackOnlyItem } from "@/types/issuePipeline";
import { kst } from "./BatchDetections";
import { conversationHref, pipelineError } from "./pipelineShared";

/** One environment's feedback-only records: SAMS answers each environment separately. */
export type FeedbackOnlyEnvironment = {
  environment: CandidateSourceEnvironment;
  items: FeedbackOnlyItem[];
  total: number | null;
  cursor: string | null;
  loading: boolean;
  error: string;
  /** The server has no feedback-only GET yet. */
  unavailable: boolean;
};

/** The card's number: a count, `unavailable` before the server has the list, `null` when unknown. */
export type FeedbackOnlyCount = number | "unavailable" | null;

export function feedbackOnlyCount(state: FeedbackOnlyEnvironment[]): FeedbackOnlyCount {
  if (!state.length || state.some((env) => env.loading || env.error)) return null;
  if (state.some((env) => env.unavailable)) return "unavailable";
  // Only a sum of every environment asked for is the count of the filter; a missing total is not 0.
  if (state.some((env) => env.total === null)) return null;
  return state.reduce((sum, env) => sum + env.total!, 0);
}

const pending = (environment: CandidateSourceEnvironment): FeedbackOnlyEnvironment => (
  { environment, items: [], total: null, cursor: null, loading: true, error: "", unavailable: false }
);

/**
 * Reads `GET /feedback-inbox/{environment}/feedback-only` for each environment.
 * Without `listing` it asks one row per environment, for the count only.
 * `environment` undefined is 전체: the environments this login may read, since
 * SAMS answers prod only with prod access (403 PROD_ACCESS_FORBIDDEN).
 */
export function useFeedbackOnly(api: AdminApi, environment: CandidateSourceEnvironment | undefined, listing: boolean) {
  const [state, setState] = useState<FeedbackOnlyEnvironment[]>([]);
  /** 전체 environments left out because this login cannot read them. */
  const [omitted, setOmitted] = useState<CandidateSourceEnvironment[]>([]);
  const request = useRef(0);
  const fetchPage = useCallback(async (environment: CandidateSourceEnvironment, cursor?: string | null) => {
    try {
      const result = await api.listFeedbackOnly(environment, { cursor, limit: listing ? 50 : 1 });
      return { items: listing ? result.items : [], total: result.total, cursor: listing ? result.next_cursor : null, error: "", unavailable: false };
    } catch (err) {
      const unavailable = err instanceof ApiError && err.status === 404;
      return { items: [], total: null, cursor: null, error: unavailable ? "" : pipelineError(err), unavailable };
    }
  }, [api, listing]);

  const load = useCallback(async () => {
    const id = ++request.current;
    let asked: CandidateSourceEnvironment[] = environment ? [environment] : ["dev", "prod"];
    if (!environment) {
      // Unknown access asks both; SAMS's own answer then decides.
      try {
        asked = await api.readableEnvironments();
      } catch {
        // keep both
      }
      if (id !== request.current) return;
    }
    setOmitted(environment ? [] : (["dev", "prod"] as const).filter((env) => !asked.includes(env)));
    setState(asked.map(pending));
    const pages = await Promise.all(asked.map((environment) => fetchPage(environment)));
    if (id !== request.current) return;
    setState(asked.map((environment, index) => ({ environment, loading: false, ...pages[index] })));
  }, [api, environment, fetchPage]);

  const more = useCallback(async (environment: CandidateSourceEnvironment) => {
    const id = request.current;
    const current = state.find((env) => env.environment === environment);
    if (!current?.cursor) return;
    setState((rows) => rows.map((env) => env.environment === environment ? { ...env, loading: true } : env));
    const page = await fetchPage(environment, current.cursor);
    if (id !== request.current) return;
    setState((rows) => rows.map((env) => env.environment !== environment ? env : page.error
      ? { ...env, loading: false, error: page.error }
      : { ...env, loading: false, items: [...env.items, ...page.items], total: page.total, cursor: page.cursor }));
  }, [state, fetchPage]);

  return { state, omitted, load, more };
}

/**
 * 피드백만 (TC 아님): 👎 on a message the AI sent first, so there is no user
 * question to replay. Read-only: these never become a TC here.
 */
export function FeedbackOnlyPanel({ state, omitted, onMore }: {
  state: FeedbackOnlyEnvironment[]; omitted: CandidateSourceEnvironment[];
  onMore: (environment: CandidateSourceEnvironment) => void;
}) {
  return (
    <section aria-label="피드백만 목록" className="grid gap-3">
      <p className="text-xs text-muted-foreground">
        AI가 먼저 보낸 메시지에 달린 👎입니다. 재실행할 사용자 질문이 없어 TC로 만들지 않습니다. 건수는 지금 해제되지 않은
        분류 기록 수이며, 일자 필터는 적용되지 않습니다.
      </p>
      {omitted.length > 0 && (
        <p className="text-xs text-muted-foreground">{omitted.join(", ")} 조회 권한이 없어 {omitted.join(", ")} 기록은 포함하지 않았습니다.</p>
      )}
      {state.map((env) => (
        <div key={env.environment} className="grid gap-2">
          {state.length > 1 && (
            <h3 className="text-sm font-semibold">
              {env.environment} {env.total !== null && <span className="font-normal text-muted-foreground">· {env.total}건</span>}
            </h3>
          )}
          {env.error && <Banner tone="error">피드백만 ({env.environment}): {env.error}</Banner>}
          {env.unavailable && (
            <Card className="p-4 text-sm text-muted-foreground">
              {env.environment} 서버에 피드백만 목록 API가 아직 없습니다.
            </Card>
          )}
          {env.items.map((item) => <FeedbackOnlyRow key={item.quarantine_id} item={item} />)}
          {!env.loading && !env.error && !env.unavailable && env.items.length === 0 && (
            <Card className="p-4 text-sm text-muted-foreground">{env.environment}: 피드백만 기록이 없습니다.</Card>
          )}
          {env.loading && <p role="status" className="text-sm">불러오는 중…</p>}
          {env.cursor && (
            <Button variant="outline" disabled={env.loading} onClick={() => onMore(env.environment)}>
              {env.environment} 피드백 더 보기
            </Button>
          )}
        </div>
      ))}
    </section>
  );
}

function FeedbackOnlyRow({ item }: { item: FeedbackOnlyItem }) {
  const { feedback } = item;
  return (
    <article aria-label="피드백만 기록" className="grid gap-1.5 rounded-xl border border-border bg-card p-4 text-sm">
      <div className="flex flex-wrap items-center gap-1.5">
        <Badge size="compact">피드백만</Badge>
        {(feedback.categories ?? []).map((category) => <Badge key={category} size="compact">{category}</Badge>)}
        <span className="text-xs text-muted-foreground">{item.source_environment}</span>
      </div>
      <p className="break-words whitespace-pre-wrap">{feedback.comment || <span className="text-muted-foreground">댓글 없음</span>}</p>
      <p className="text-xs text-muted-foreground">{item.reason}</p>
      <span className="text-xs text-muted-foreground">
        {[feedback.author_email, feedback.created_at && `${kst(feedback.created_at)} 작성`, `${kst(item.classified_at)} 분류`]
          .filter(Boolean).join(" · ")}
      </span>
      {item.session_id && (
        <a className="justify-self-start text-xs text-link hover:underline"
          href={conversationHref(item.source_environment, item.session_id, item.assistant_message_id)}>
          👎 받은 AI 메시지 열기 ↗
        </a>
      )}
    </article>
  );
}
